import { AMOUNT_NUMBER_REGEX, DESKTOP_MIN_WIDTH } from 'constants';
import {
  FORM_ADD_PAYTO_MAX_AMOUNT_FIELD,
  FORM_ADD_PAYTO_MAX_AMOUNT_NAME,
  PAY_BY_PAYTO_AMOUNT_MAX_LIMIT,
} from 'features/to/const';
import usePaytoStore from 'features/to/hooks/payment/usePaytoStore';
import { format } from 'helpers';
import { Fade } from 'helpers/animation';
import useAddTransaction from 'hooks/form-add-transaction/useAddTransaction';
import useLang from 'hooks/useLang';
import { useState } from 'react';
import Form from 'react-bootstrap/Form';
import styled from 'styled-components';
import { paragraph500Mixin } from 'styles';
import { numberUtils } from 'utils';

const FormInputAmountValidation = ({
  field,
  information,
  setInformation,
  validation,
  setValidation,
  maxAmountInitial = 0,
}) => {
  const { t } = useLang();

  const [isPaste, setPaste] = useState(false);

  const { getTotalToPay } = useAddTransaction();

  const { setPaytoMaxAmountEdited } = usePaytoStore();

  const totalToPay = getTotalToPay();

  const { maxLimit, minLimit } = {
    maxLimit: PAY_BY_PAYTO_AMOUNT_MAX_LIMIT,
    minLimit: totalToPay,
  };

  const handleMaxAmountValidation = (information, validation) => {
    let newValidation = { ...validation };

    const maxAmount = numberUtils.mathRound(
      information[FORM_ADD_PAYTO_MAX_AMOUNT_NAME] || 0,
    );

    if ((maxLimit && !minLimit) || (maxLimit && minLimit)) {
      if (maxAmount > maxLimit) {
        newValidation = {
          ...newValidation,
          [FORM_ADD_PAYTO_MAX_AMOUNT_NAME]: t(
            FORM_ADD_PAYTO_MAX_AMOUNT_FIELD.msgMaxLimited,
            {
              max: format.toAmountStr(maxLimit),
            },
          ),
        };
        return newValidation;
      }
    }

    if ((!maxLimit && minLimit) || (maxLimit && minLimit)) {
      if (maxAmount < minLimit) {
        newValidation = {
          ...newValidation,
          [FORM_ADD_PAYTO_MAX_AMOUNT_NAME]: t(
            FORM_ADD_PAYTO_MAX_AMOUNT_FIELD.msgMinLimited,
            {
              min: format.toAmountStr(minLimit),
            },
          ),
        };
        return newValidation;
      }
    }

    if (maxAmount <= maxLimit) {
      newValidation = {
        ...newValidation,
        [FORM_ADD_PAYTO_MAX_AMOUNT_NAME]: '',
      };
      return newValidation;
    }

    return newValidation;
  };

  const handleMaxAmountOnChange = (value) => {
    if (isPaste && value > maxLimit) {
      value = Number(maxLimit);
    }

    if (value > maxLimit && validation?.[field?.name]) {
      value = Number(`${value}`.slice(0, -1));
    }

    document.getElementsByName(FORM_ADD_PAYTO_MAX_AMOUNT_NAME)[0].value =
      format.toAmountCentStr(value);

    value = numberUtils.mathRound(value);

    return [value];
  };

  const handleOnChange = (e, valueRoundUp = 0) => {
    const { value, name } = e.target;

    let valueTrim =
      valueRoundUp || (value ? format.toStrAmount(value.trim()) : '');
    let maxValue;

    setPaytoMaxAmountEdited(true);

    if (valueTrim?.length <= 1 && !numberUtils.isNumber(valueTrim)) {
      document.getElementsByName(name)[0].value = '';

      return;
    }

    let newInformation = { ...information };
    let newValidation = { ...validation };

    if (!valueTrim) {
      newValidation = {
        ...newValidation,
        [FORM_ADD_PAYTO_MAX_AMOUNT_NAME]: '',
      };
      setValidation(newValidation);

      document.getElementsByName(FORM_ADD_PAYTO_MAX_AMOUNT_NAME)[0].value = '';
      newInformation = {
        ...newInformation,
        [FORM_ADD_PAYTO_MAX_AMOUNT_NAME]: '',
        feeAmount: 0,
      };
      setInformation(newInformation);

      return;
    }

    if (!numberUtils.isNumber(valueTrim)) {
      valueTrim = valueTrim.match(AMOUNT_NUMBER_REGEX)[0];

      document.getElementsByName(name)[0].value = valueTrim;
    }

    if (value.includes(' ')) {
      document.getElementsByName(name)[0].value = valueTrim;
    }

    [maxValue] = handleMaxAmountOnChange(valueTrim);
    newInformation = {
      ...newInformation,
      [FORM_ADD_PAYTO_MAX_AMOUNT_NAME]: maxValue,
    };
    setInformation(newInformation);

    newValidation = handleMaxAmountValidation(newInformation, newValidation);
    setValidation(newValidation);
  };

  return (
    <FormGroupStyled>
      <CurrencyCode>AUD</CurrencyCode>
      <FormControl
        type={field?.type}
        inputMode="decimal"
        autoComplete="off"
        name={field?.name}
        defaultValue={
          information && information[field?.name]
            ? format.toAmountCentStr(information[field?.name])
            : format.toAmountCentStr(maxAmountInitial)
        }
        placeholder={field?.placeholder}
        isInvalid={validation && validation?.[field?.name]}
        onChange={handleOnChange}
        onPaste={() => setPaste(true)}
        readOnly={field?.isReadOnly}
      />
      <FormControlFeedback hidden={false} type="invalid">
        {t(validation?.[field?.name])}
      </FormControlFeedback>
    </FormGroupStyled>
  );
};

const FormGroupStyled = styled(Form.Group)`
  margin-bottom: 0px;
  position: relative;
  ${Fade};
`;

const FormControl = styled(Form.Control)`
  ${paragraph500Mixin};
  height: 44px;
  border: 1px solid var(--ds-c-blue-remox-light);
  border-radius: 12px;
  padding-right: 10px;
  padding-left: 48px;
  background: var(--ds-c-white);

  &:focus {
    background: var(--ds-c-white) !important;
    border: 1px solid var(--ds-c-grey-dark) !important;
    outline: none !important;
    box-shadow: none !important;
  }

  &:invalid,
  &:invalid:focus,
  &.is-invalid {
    border: 1px solid var(--ds-c-red) !important;
    outline: none !important;
    box-shadow: none !important;
    background-image: none !important;
    padding-right: 10px !important;
    padding-left: 48px !important;
  }

  &[readonly] {
    background: var(--ds-c-white) !important;
    cursor: not-allowed;
  }

  &::placeholder {
    ${paragraph500Mixin};
    color: var(--ds-c-grey-hover);
  }
`;

const FormControlFeedback = styled(Form.Control.Feedback)`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;

  color: var(--ds-c-red);
  position: absolute;
  margin-top: 4px;

  @media screen and (max-width: ${DESKTOP_MIN_WIDTH +
    1}px) and (min-width: ${DESKTOP_MIN_WIDTH - 1}px) {
    font-size: 14px;
    line-height: 17px;
  }
`;

const CurrencyCode = styled.div`
  ${paragraph500Mixin};
  width: fit-content;
  height: 44px;
  background-color: transparent;
  justify-content: flex-start;
  align-items: center;
  padding-right: 0px;
  display: flex;
  position: absolute;
  top: auto;
  bottom: 0%;
  left: 10px;
`;

export default FormInputAmountValidation;
